import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { ownerDocumentOf, ownerWindowOf } from '../../utils/ownerWindow';
import { useWideContent } from '../shared/PopoutWindow';
import { X, Users, Loader2, AlertCircle, Info, Check, Search, ChevronDown, ChevronRight, ChevronLeft, BarChart3 } from 'lucide-react';
import { useForecastV3, useMonthlyVersions } from '../../context/ForecastV3Context';
import type { ForecastAccount, LaborPosition, LaborPositionConfig, LaborPositionDriverType, LaborRevenueScope } from '../../types';
// @ts-ignore - JavaScript module without types
import API_ENDPOINTS from '../../../../config/apiConfig';
import { BarChart as KitBarChart } from '../../../../components/charts/bar-chart';
import { Bar as KitBar } from '../../../../components/charts/bar';
import { Grid as KitGrid } from '../../../../components/charts/grid';
import { BarXAxis as KitBarXAxis } from '../../../../components/charts/bar-x-axis';
import { YAxis as KitYAxis } from '../../../../components/charts/y-axis';
import { ChartTooltip as KitChartTooltip } from '../../../../components/charts/tooltip';

// @ts-ignore - JavaScript module without types
import { api } from '../../../../utils/api';
import { useForecastChartPalette } from '../../utils/chartPalette';

// The kit is JS with defaulted props; TS infers every default-less prop as required — loosen here.
type AnyComponent = React.ComponentType<any>;
const BarChart = KitBarChart as unknown as AnyComponent;
const Bar = KitBar as unknown as AnyComponent;
const Grid = KitGrid as unknown as AnyComponent;
const BarXAxis = KitBarXAxis as unknown as AnyComponent;
const YAxis = KitYAxis as unknown as AnyComponent;
const ChartTooltip = KitChartTooltip as unknown as AnyComponent;

// ─── Constants ──────────────────────────────────────────────────────────────

const FTE_HOURS_PER_YEAR = 2080;

const DRIVER_META: Record<LaborPositionDriverType, {
  short: string; long: string; unit: string; color: string; bgColor: string; math: string;
}> = {
  min_por:     { short: 'MPOR',  long: 'Minutes per occupied room', unit: 'min', color: 'var(--lodge-onyx-500)', bgColor: 'var(--lodge-onyx-50)',  math: 'min × occ rooms ÷ 60 = hrs' },
  hrs_por:     { short: 'HPOR',  long: 'Hours per occupied room',   unit: 'hrs', color: 'var(--lodge-orange-400)', bgColor: 'var(--lodge-orange-50)',  math: 'hrs × occ rooms = hrs' },
  fte:         { short: 'FTE',   long: 'Full-time equivalents (2,080 hrs/yr)', unit: 'FTE', color: 'var(--color-warning)', bgColor: 'var(--color-warning-bg)', math: 'FTE × 2,080 hrs ÷ 12 mo' },
  pct_revenue: { short: '% Rev', long: 'Percent of revenue',        unit: '%',   color: 'var(--color-success)', bgColor: 'var(--color-success-bg)',  math: '' },
};

const RANGE_OPTIONS = [
  { key: '30d', label: '30D', days: 30 },
  { key: '90d', label: '90D', days: 90 },
  { key: '6m',  label: '6M',  days: 182 },
  { key: '1y',  label: '1Y',  days: 365 },
] as const;
type RangeKey = typeof RANGE_OPTIONS[number]['key'];

// ─── Position stats types (from /laborPositionStats) ────────────────────────

interface EmployeeStat {
  employeeId:  number;
  name:        string;
  status:      string | null;
  hireDate?:   string | null;
  terminationDate?: string | null;
  hours:       number;
  pay:         number;
  avgRate:     number | null;
  hrsPerWeek:  number;
  hrsPerMonth: number;
  fte:         number;
  /** Calendar days in the employee's own first→last shift span (rates are per this span) */
  spanDays?:   number;
  daysWorked:  number;
  /** ISO dates — first shift EVER for this position (not just the window), last shift in window */
  firstShiftDate?: string | null;
  lastShiftDate?:  string | null;
  /** Hours per day, aligned to PositionStats.dates */
  dailyHours?: number[];
  /** Active roster member with no payroll in the window */
  noHoursInWindow?: boolean;
}

interface PositionStats {
  days:          number;
  occupiedRooms: number;
  /** ISO dates for each dailyHours bucket */
  dates:         string[];
  /** Occupied rooms per day, aligned to `dates` */
  occupiedRoomsByDay: number[];
  /** First→last day with payroll for this position inside the window; rates are normalized to it */
  coverage: { firstDay: string; lastDay: string; days: number; windowDays: number; occupiedRooms: number } | null;
  totals: {
    /** Employees with hours in the window */
    headcount:   number;
    /** Everyone listed — active roster + terminated with hours */
    roster?:     number;
    hours:       number;
    pay:         number;
    avgRate:     number | null;
    hrsPerWeek:  number;
    hrsPerMonth: number;
    fte:         number;
    mpor:        number | null;
    hpor:        number | null;
  } | null;
  employees: EmployeeStat[];
}

// ─── DriverTypeToggle ────────────────────────────────────────────────────────

function DriverTypeToggle({
  value, onChange,
}: { value: LaborPositionDriverType; onChange: (t: LaborPositionDriverType) => void }) {
  const types: LaborPositionDriverType[] = ['min_por', 'hrs_por', 'fte', 'pct_revenue'];
  return (
    <div className="flex rounded-md overflow-hidden border border-slate-200" style={{ width: 196 }}>
      {types.map((t, i) => {
        const m = DRIVER_META[t];
        const active = t === value;
        return (
          <button
            key={t}
            type="button"
            title={m.long}
            onClick={() => onChange(t)}
            className="flex-1 text-[11px] font-semibold py-[7px] transition-all select-none"
            style={{
              background: active ? m.color : '#fff',
              color:      active ? '#fff' : 'var(--fv3-n-500, #64748b)',
              borderRight: i < 3 ? '1px solid var(--fv3-n-100, #e2e8f0)' : undefined,
            }}
          >
            {m.short}
          </button>
        );
      })}
    </div>
  );
}

// ─── RevenueScopePicker (drill-in, multi-select accounts) ───────────────────

interface RevenueScopePickerProps {
  scope:              LaborRevenueScope;
  deptName?:          string;
  accountIds:         number[];
  revenueDepartments: string[];
  accounts:           ForecastAccount[];
  onChange:           (scope: LaborRevenueScope, deptName?: string, accountIds?: number[]) => void;
}

function RevenueScopePicker({
  scope, deptName, accountIds, revenueDepartments, accounts, onChange,
}: RevenueScopePickerProps) {
  const [open, setOpen]     = useState(false);
  const [panel, setPanel]   = useState<'main' | 'accounts'>('main');
  const [search, setSearch] = useState('');
  // Draft selection while the accounts panel is open — committed on Done
  const [draft, setDraft]   = useState<number[]>([]);
  const ref                 = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const h = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    const doc = ownerDocumentOf(ref.current);
    doc.addEventListener('mousedown', h);
    return () => doc.removeEventListener('mousedown', h);
  }, []);

  useEffect(() => { if (!open) { setPanel('main'); setSearch(''); } }, [open]);

  const revAccounts = accounts.filter(a => a.accountType === 'Revenue');
  const filtered    = revAccounts.filter(a =>
    !search ||
    a.accountName.toLowerCase().includes(search.toLowerCase()) ||
    a.accountNumber.toLowerCase().includes(search.toLowerCase())
  );

  const label =
    scope === 'total'      ? 'Total Revenue' :
    scope === 'department' ? (deptName ? `Dept: ${deptName}` : 'Select dept…') :
    accountIds.length === 0 ? 'Select accounts…' :
    accountIds.length === 1
      ? (revAccounts.find(x => x.chartOfAccountId === accountIds[0])?.accountName ?? '1 account')
      : `${accountIds.length} accounts`;

  const openAccountsPanel = () => {
    setDraft(accountIds);
    setPanel('accounts');
    setSearch('');
  };

  const toggleDraft = (id: number) => {
    setDraft(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const commitAccounts = () => {
    onChange('account', undefined, draft);
    setOpen(false);
  };

  return (
    <div ref={ref} className="relative" style={{ width: 188 }}>
      <button
        type="button"
        onClick={() => { setOpen(o => !o); }}
        className="w-full flex items-center justify-between gap-1 px-2.5 py-[6px] bg-white border border-slate-200 rounded-md text-[12px] outline-none transition-colors"
        style={open ? { borderColor: 'var(--fv3-accent, #f28c38)', boxShadow: '0 0 0 2px var(--fv3-accent-tint, rgba(242,140,56,0.1))' } : {}}
      >
        <span className="truncate text-left" style={{ color: scope === 'total' || (scope === 'account' && accountIds.length > 0) || (scope === 'department' && deptName) ? 'var(--fv3-n-900, #1e293b)' : 'var(--fv3-n-400, #94a3b8)' }}>
          {label}
        </span>
        <ChevronDown size={11} className="text-slate-400 flex-shrink-0" />
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute top-full left-0 mt-1 z-50 bg-white border border-slate-200 rounded-lg shadow-lg overflow-hidden" style={{ width: 260 }}>

            {panel === 'main' && (
              <>
                {/* Total Revenue */}
                <button
                  className="w-full text-left px-3 py-2 text-[12px] flex items-center gap-2 hover:bg-slate-50 transition-colors"
                  style={scope === 'total' ? { background: 'var(--fv3-accent-tint, rgba(242,140,56,0.06))', fontWeight: 600, color: 'var(--fv3-accent, #f28c38)' } : { color: 'var(--fv3-n-900, #1e293b)' }}
                  onClick={() => { onChange('total'); setOpen(false); }}
                >
                  <span className="w-2 h-2 rounded-full bg-emerald-400 flex-shrink-0" />
                  Total Revenue
                </button>

                {/* By Department */}
                {revenueDepartments.length > 0 && (
                  <>
                    <div className="px-3 pt-1.5 pb-0.5 text-[10px] font-semibold text-slate-400 uppercase tracking-wider border-t border-slate-100 bg-slate-50">
                      By Department
                    </div>
                    <div className="overflow-y-auto" style={{ maxHeight: 168 }}>
                      {revenueDepartments.map(dept => (
                        <button
                          key={dept}
                          className="w-full text-left px-3 py-1.5 text-[12px] hover:bg-slate-50 transition-colors"
                          style={scope === 'department' && deptName === dept ? { background: 'var(--fv3-accent-tint, rgba(242,140,56,0.06))', fontWeight: 600, color: 'var(--fv3-accent, #f28c38)' } : { color: 'var(--fv3-n-900, #1e293b)' }}
                          onClick={() => { onChange('department', dept); setOpen(false); }}
                        >
                          {dept}
                        </button>
                      ))}
                    </div>
                  </>
                )}

                {/* Accounts → drill-in */}
                {revAccounts.length > 0 && (
                  <button
                    className="w-full text-left px-3 py-2 text-[12px] flex items-center justify-between gap-2 border-t border-slate-100 hover:bg-slate-50 transition-colors"
                    style={scope === 'account' ? { background: 'var(--fv3-accent-tint, rgba(242,140,56,0.06))', fontWeight: 600, color: 'var(--fv3-accent, #f28c38)' } : { color: 'var(--fv3-n-900, #1e293b)' }}
                    onClick={openAccountsPanel}
                  >
                    <span className="flex items-center gap-2">
                      Accounts
                      {scope === 'account' && accountIds.length > 0 && (
                        <span className="text-[10px] font-semibold px-1.5 py-px rounded-full" style={{ background: 'rgba(16,185,129,0.1)', color: '#10B981' }}>
                          {accountIds.length}
                        </span>
                      )}
                    </span>
                    <ChevronRight size={12} className="text-slate-400 flex-shrink-0" />
                  </button>
                )}
              </>
            )}

            {panel === 'accounts' && (
              <>
                {/* Panel header: back + search */}
                <div className="flex items-center gap-1 px-2 py-1.5 border-b border-slate-100 bg-slate-50">
                  <button
                    className="p-1 rounded hover:bg-slate-200/60 text-slate-500 transition-colors flex-shrink-0"
                    onClick={() => setPanel('main')}
                    title="Back"
                  >
                    <ChevronLeft size={13} />
                  </button>
                  <div className="relative flex-1">
                    <Search size={11} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                    <input
                      autoFocus
                      type="text"
                      value={search}
                      onChange={e => setSearch(e.target.value)}
                      placeholder="Search revenue accounts…"
                      className="w-full pl-6 pr-2 py-1 text-[11px] bg-white border border-slate-200 rounded focus:outline-none placeholder-slate-400"
                    />
                  </div>
                </div>

                <div className="overflow-y-auto" style={{ maxHeight: 200 }}>
                  {filtered.length === 0 ? (
                    <p className="px-3 py-2 text-[11px] text-slate-400">No accounts found</p>
                  ) : filtered.map(a => {
                    const checked = draft.includes(a.chartOfAccountId);
                    return (
                      <button
                        key={a.chartOfAccountId}
                        className="w-full text-left px-3 py-1.5 flex items-center gap-2.5 hover:bg-slate-50 transition-colors"
                        onClick={() => toggleDraft(a.chartOfAccountId)}
                      >
                        <span
                          className="w-3.5 h-3.5 rounded-sm border flex items-center justify-center flex-shrink-0 transition-colors"
                          style={checked ? { background: 'var(--fv3-accent, #f28c38)', borderColor: 'var(--fv3-accent, #f28c38)' } : { borderColor: 'var(--fv3-n-200, #cbd5e1)', background: '#fff' }}
                        >
                          {checked && <Check size={10} strokeWidth={3} color="#fff" />}
                        </span>
                        <span className="min-w-0">
                          <span className="block truncate text-[12px]" style={{ color: 'var(--fv3-n-900, #1e293b)' }}>{a.accountName}</span>
                          <span className="text-[10px] text-slate-400">{a.accountNumber}</span>
                        </span>
                      </button>
                    );
                  })}
                </div>

                {/* Footer: count + done */}
                <div className="flex items-center justify-between px-3 py-2 border-t border-slate-100 bg-slate-50">
                  <span className="text-[11px] text-slate-400">
                    {draft.length} selected
                  </span>
                  <button
                    onClick={commitAccounts}
                    disabled={draft.length === 0}
                    className="px-2.5 py-1 text-[11px] font-semibold rounded text-white transition-colors disabled:opacity-40"
                    style={{ background: 'var(--fv3-accent, #f28c38)' }}
                  >
                    Done
                  </button>
                </div>
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
}

// ─── Employee tenure / trend helpers ────────────────────────────────────────

const fmtShort = (iso: string) => {
  const d = new Date(iso + 'T00:00:00');
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};
/** Hire/termination dates carry the year — tenure spans years, chart ticks don't */
const fmtFull = (iso: string) => {
  const d = new Date(iso + 'T00:00:00');
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
};

const isTerminatedStatus = (status: string | null | undefined) => !!status && /^(t|term|inactive|separated)/i.test(status);

interface EmployeeStatusLine { text: string; tone: 'active' | 'terminated'; }

/** Status + hire/term line under the employee name: green while active, red once terminated */
function employeeStatusLine(emp: EmployeeStat): EmployeeStatusLine {
  const terminated = isTerminatedStatus(emp.status) || !!emp.terminationDate;
  if (terminated) {
    const label = emp.status && !/^a/i.test(emp.status) ? emp.status : 'Terminated';
    return { text: emp.terminationDate ? `${label} · ${fmtFull(emp.terminationDate)}` : label, tone: 'terminated' };
  }
  const started = emp.hireDate || emp.firstShiftDate;
  const label = emp.status || 'Active';
  return { text: started ? `${label} · hired ${fmtFull(started)}` : label, tone: 'active' };
}

const STATUS_TONE: Record<EmployeeStatusLine['tone'], string> = {
  active: 'var(--color-success, #10B981)',
  terminated: 'var(--color-danger, #DC2626)',
};

const NEUTRAL_FILLS = ['#26262D', '#72727F', '#A0A0AC', '#B8B8C4', '#CFCFD8', '#E0E0E6'];
const FOCUS_FILL = 'var(--lodge-orange-400, var(--fv3-accent, #FF7A00))';

/** Monday-start ISO week key for an ISO date */
const weekKeyOf = (iso: string) => {
  const d = new Date(iso + 'T00:00:00Z');
  d.setUTCDate(d.getUTCDate() - ((d.getUTCDay() + 6) % 7));
  return d.toISOString().slice(0, 10);
};

interface EmployeeHoursChartProps {
  employees: EmployeeStat[];
  dates: string[];
  occupiedRoomsByDay: number[];
  /** Roll days up to Monday weeks (long windows) */
  weekly: boolean;
  focusId: number | null;
  onFocus: (id: number | null) => void;
}

/** Total hours worked per day (bklit BarChart); clicking an employee row filters the bars to that person */
function EmployeeHoursChart({ employees, dates, occupiedRoomsByDay, weekly, focusId, onFocus }: EmployeeHoursChartProps) {
  const focused = focusId != null ? employees.find(e => e.employeeId === focusId) ?? null : null;
  // New look: bars follow the chart palette; Classic keeps ink bars + brand focus.
  const chartPalette = useForecastChartPalette();
  const baseFill = chartPalette.inset ? chartPalette.colors.secondary : NEUTRAL_FILLS[0];
  const focusFill = chartPalette.inset ? chartPalette.colors.primary : FOCUS_FILL;
  const { rows, xLabels } = useMemo(() => {
    const bucketKeys: string[] = [];
    const bucketIdx = new Map<string, number>();
    for (const iso of dates) {
      const k = weekly ? weekKeyOf(iso) : iso;
      if (!bucketIdx.has(k)) { bucketIdx.set(k, bucketKeys.length); bucketKeys.push(k); }
    }
    const rows = bucketKeys.map(k => ({ key: k, hours: 0, headcount: 0, rooms: 0, days: 0 }));
    dates.forEach((iso, di) => {
      const b = bucketIdx.get(weekly ? weekKeyOf(iso) : iso);
      if (b === undefined) return;
      rows[b].rooms += occupiedRoomsByDay[di] ?? 0;
      rows[b].days += 1;
    });
    const source = focused ? [focused] : employees;
    for (const e of source) {
      const seen = new Set<number>();
      (e.dailyHours ?? []).forEach((h, di) => {
        const b = bucketIdx.get(weekly ? weekKeyOf(dates[di]) : dates[di]);
        if (b === undefined || h <= 0) return;
        rows[b].hours = Math.round((rows[b].hours + h) * 10) / 10;
        if (!seen.has(b)) { seen.add(b); rows[b].headcount += 1; }
      });
    }
    // Label every ~7th day (weekly: every bucket) so a 90-day axis stays legible
    const step = weekly ? Math.max(1, Math.ceil(bucketKeys.length / 12)) : bucketKeys.length > 40 ? 14 : 7;
    const last = bucketKeys.length - 1;
    const xLabels = bucketKeys.map((k, i) => (i % step === 0 || (i === last && last % step >= 3) ? fmtShort(k) : ''));
    return { rows, xLabels };
  }, [employees, focused, dates, occupiedRoomsByDay, weekly]);

  if (rows.length < 2 || employees.length === 0) return null;

  return (
    <div className="px-3.5 pb-2">
      <div className="flex items-center justify-between mb-1">
        <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">
          Hours worked · {weekly ? 'per week' : 'per day'}{focused ? ` · ${focused.name}` : ' · all employees'}
        </span>
        {focused && (
          <button type="button" onClick={() => onFocus(null)} className="text-[10px] font-medium text-slate-400 hover:text-slate-600">
            Show all
          </button>
        )}
      </div>
      <div className="rn-chart-body" style={{ height: 160 }}>
        <BarChart
          className="rn-bklit"
          data={rows}
          xDataKey="key"
          xLabels={xLabels}
          barGap={rows.length > 40 ? 0.15 : 0.3}
          margin={{ top: 8, right: 8, bottom: 34, left: 34 }}
          aspectRatio={null}
          style={{ height: '100%' }}
          animationDuration={600}
        >
          <Grid horizontal numTicksRows={4} />
          <BarXAxis showAllLabels maxLabels={rows.length} />
          <YAxis numTicks={4} peak formatValue={(v: number) => `${Math.round(v)}h`} />
          <ChartTooltip
            title={(point: { key: string }) => (weekly ? `Wk of ${fmtShort(point.key)}` : fmtShort(point.key))}
            showDots={false}
            showDatePill={false}
            rows={(point: { hours: number; headcount: number; rooms: number; days: number }) => {
              const n1 = (v: number, d = 1) => v.toLocaleString(undefined, { maximumFractionDigits: d });
              const accent = focused ? focusFill : baseFill;
              // Daily FTE = hours ÷ 8; weekly buckets divide by 8 × days in the bucket
              const fte = point.hours / (8 * Math.max(1, point.days));
              const out = [
                { color: accent, label: focused ? focused.name : 'Hours', value: `${n1(point.hours)} h` },
                { color: 'transparent', label: 'Occ rooms', value: point.rooms > 0 ? n1(point.rooms, 0) : '—' },
                { color: 'transparent', label: 'MPOR', value: point.rooms > 0 ? `${n1((point.hours * 60) / point.rooms, 2)} min` : '—' },
                { color: 'transparent', label: 'HPOR', value: point.rooms > 0 ? `${n1(point.hours / point.rooms, 3)} hrs` : '—' },
                { color: 'transparent', label: 'FTE', value: n1(fte, 2) },
              ];
              if (!focused) out.push({ color: 'transparent', label: 'Employees', value: String(point.headcount) });
              return out;
            }}
          />
          <Bar dataKey="hours" fill={focused ? focusFill : baseFill} lineCap={2} />
        </BarChart>
      </div>
    </div>
  );
}

// ─── StaffingDrawer — per-position employee analytics ───────────────────────

interface StaffingDrawerProps {
  positionId: number;
  stats:      PositionStats | null;
  isLoading:  boolean;
  range:      RangeKey;
  onRangeChange: (r: RangeKey) => void;
  onUseValue: (driverType: LaborPositionDriverType, value: number) => void;
}

function StaffingDrawer({ positionId, stats, isLoading, range, onRangeChange, onUseValue }: StaffingDrawerProps) {
  const totals = stats?.totals ?? null;
  const employees = stats?.employees ?? [];
  const maxHours = employees.reduce((m, e) => Math.max(m, e.hours), 0);
  const dates = stats?.dates ?? [];
  const [focusId, setFocusId] = useState<number | null>(null);
  useEffect(() => { setFocusId(null); }, [positionId, range]);

  // The three payroll-derived benchmarks the user can adopt as driver values
  const benchmarks: { type: LaborPositionDriverType; value: number | null; suffix: string }[] = [
    { type: 'min_por', value: totals?.mpor ?? null, suffix: 'min/room' },
    { type: 'hrs_por', value: totals?.hpor ?? null, suffix: 'hrs/room' },
    { type: 'fte',     value: totals?.fte  ?? null, suffix: 'FTE' },
  ];

  return (
    <tr>
      <td colSpan={5} className="p-0">
        <div className="mx-4 mb-3 rounded-lg border border-slate-200 overflow-hidden" style={{ background: 'var(--fv3-n-50, #F8FAFC)', boxShadow: 'inset 0 1px 3px rgba(15,23,42,0.04)' }}>

          {/* Drawer header: title + range picker */}
          <div className="flex items-center justify-between px-3.5 pt-2.5 pb-2">
            <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider flex items-center gap-1.5">
              <BarChart3 size={11} />
              Actual staffing · last {range === '6m' ? '6 months' : range === '1y' ? '12 months' : range.replace('d', ' days')}
              {stats?.coverage && stats.coverage.days < stats.coverage.windowDays - 2 && (
                <span className="normal-case tracking-normal font-medium text-slate-400">
                  · payroll covers {fmtShort(stats.coverage.firstDay)}–{fmtShort(stats.coverage.lastDay)} ({stats.coverage.days} of {stats.coverage.windowDays} days) — rates use that span
                </span>
              )}
            </span>
            <div className="flex rounded-md overflow-hidden border border-slate-200 bg-white">
              {RANGE_OPTIONS.map((opt, i) => (
                <button
                  key={opt.key}
                  onClick={() => onRangeChange(opt.key)}
                  className="px-2 py-[3px] text-[10px] font-semibold transition-colors"
                  style={{
                    background: range === opt.key ? 'var(--fv3-n-700, #334155)' : '#fff',
                    color:      range === opt.key ? '#fff' : 'var(--fv3-n-500, #64748b)',
                    borderRight: i < RANGE_OPTIONS.length - 1 ? '1px solid var(--fv3-n-100, #e2e8f0)' : undefined,
                  }}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {isLoading && (
            <div className="flex items-center justify-center py-8 gap-2 text-slate-400">
              <Loader2 size={15} className="animate-spin" />
              <span className="text-[12px]">Loading payroll history…</span>
            </div>
          )}

          {!isLoading && (!totals || (totals.roster ?? totals.headcount) === 0) && (
            <div className="px-3.5 pb-3 text-[12px] text-slate-400">
              No payroll activity for this position in the selected window.
            </div>
          )}

          {!isLoading && totals && (totals.roster ?? totals.headcount) > 0 && (
            <>
              {/* Benchmark chips — click to adopt as the driver value */}
              <div className="flex items-stretch gap-2 px-3.5 pb-2 flex-wrap">
                {benchmarks.map(b => {
                  const m = DRIVER_META[b.type];
                  const disabled = b.value == null || b.value <= 0;
                  return (
                    <button
                      key={b.type}
                      disabled={disabled}
                      onClick={() => b.value != null && onUseValue(b.type, b.value)}
                      title={disabled ? 'No occupancy data for this window' : `Set driver to ${m.short} = ${b.value}`}
                      className="group flex flex-col items-start px-2.5 py-1.5 rounded-md border bg-white text-left transition-all disabled:opacity-40 disabled:cursor-not-allowed flex-1 min-w-[96px]"
                      style={{ borderColor: 'var(--fv3-n-100, #e2e8f0)' }}
                      onMouseEnter={e => { if (!disabled) { e.currentTarget.style.borderColor = m.color; e.currentTarget.style.boxShadow = `0 0 0 2px ${m.bgColor}`; } }}
                      onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--fv3-n-100, #e2e8f0)'; e.currentTarget.style.boxShadow = 'none'; }}
                    >
                      <span className="text-[9px] font-bold uppercase tracking-wider" style={{ color: m.color }}>
                        {m.short}
                      </span>
                      <span className="text-[13px] font-bold text-slate-800 tabular-nums leading-tight">
                        {b.value == null ? '—' : b.value.toLocaleString(undefined, { maximumFractionDigits: b.type === 'hrs_por' ? 3 : 2 })}
                        <span className="text-[9px] font-medium text-slate-400 ml-1">{b.suffix}</span>
                      </span>
                      <span className="text-[9px] font-medium opacity-0 group-hover:opacity-100 transition-opacity" style={{ color: m.color }}>
                        Use as value →
                      </span>
                    </button>
                  );
                })}

                {/* Window facts — same tile anatomy as the benchmarks so the row reads as one strip */}
                <span className="self-stretch w-px bg-slate-200 mx-0.5" aria-hidden />
                {[
                  { label: 'Employees', value: String(totals.headcount) },
                  { label: 'Hours', value: totals.hours.toLocaleString(undefined, { maximumFractionDigits: 0 }) },
                  { label: 'Avg rate', value: totals.avgRate != null ? `$${totals.avgRate.toFixed(2)}` : '—', suffix: totals.avgRate != null ? '/hr' : '' },
                  { label: 'Occ rooms', value: (stats?.coverage?.occupiedRooms ?? stats?.occupiedRooms ?? 0) > 0 ? (stats?.coverage?.occupiedRooms ?? stats?.occupiedRooms ?? 0).toLocaleString() : '—' },
                ].map(f => (
                  <div key={f.label} className="flex flex-col items-start px-2.5 py-1.5 rounded-md border border-transparent flex-1 min-w-[72px]">
                    <span className="text-[9px] font-bold uppercase tracking-wider text-slate-400">{f.label}</span>
                    <span className="text-[13px] font-bold text-slate-700 tabular-nums leading-tight">
                      {f.value}{f.suffix && <span className="text-[9px] font-medium text-slate-400 ml-0.5">{f.suffix}</span>}
                    </span>
                  </div>
                ))}
              </div>

              <EmployeeHoursChart
                employees={employees}
                dates={dates}
                occupiedRoomsByDay={stats?.occupiedRoomsByDay ?? []}
                weekly={range === '6m' || range === '1y'}
                focusId={focusId}
                onFocus={setFocusId}
              />

              {/* Employee table — click a row to focus its line */}
              <table className="w-full" style={{ tableLayout: 'fixed' }}>
                <colgroup>
                  <col style={{ width: '28%' }} />
                  <col style={{ width: '13%' }} />
                  <col style={{ width: '13%' }} />
                  <col style={{ width: '13%' }} />
                  <col style={{ width: '10%' }} />
                  <col style={{ width: '23%' }} />
                </colgroup>
                <thead>
                  <tr className="border-t border-b border-slate-200/80 bg-white/60">
                    {['Employee', 'Rate', 'Hrs/Wk', 'Hrs/Mo', 'FTE', 'Share of hours'].map((h, i) => (
                      <th key={h} className={`py-1.5 text-[9px] font-semibold text-slate-400 uppercase tracking-wider ${i === 0 ? 'text-left pl-3.5' : i >= 4 ? 'text-left px-2' : 'text-right px-2'}`}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {employees.map(emp => {
                    const active = !isTerminatedStatus(emp.status) && !emp.terminationDate;
                    const line = employeeStatusLine(emp);
                    const idle = !!emp.noHoursInWindow;
                    return (
                      <tr
                        key={emp.employeeId}
                        className="border-b border-slate-100/80 last:border-b-0 cursor-pointer transition-colors"
                        style={focusId === emp.employeeId ? { background: 'var(--lodge-orange-50, var(--fv3-accent-wash, #FFF4EA))' } : undefined}
                        onClick={() => setFocusId(prev => (prev === emp.employeeId ? null : emp.employeeId))}
                        title="Click to focus this employee's line"
                      >
                        <td className="py-1.5 pl-3.5 pr-2">
                          <span className="flex items-center gap-1.5 min-w-0">
                            <span
                              className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                              style={{ background: active ? 'var(--color-success, #10B981)' : 'var(--color-danger, #DC2626)' }}
                              title={emp.status || 'Active'}
                            />
                            <span className="text-[12px] text-slate-700 truncate">{emp.name}</span>
                          </span>
                          <span className="block pl-3 text-[10px] leading-tight tabular-nums truncate" style={{ color: STATUS_TONE[line.tone], fontWeight: 500 }}>
                            {line.text}
                          </span>
                        </td>
                        <td className="py-1.5 px-2 text-right text-[12px] text-slate-600 tabular-nums">
                          {emp.avgRate != null ? `$${emp.avgRate.toFixed(2)}` : '—'}
                        </td>
                        <td className="py-1.5 px-2 text-right text-[12px] text-slate-600 tabular-nums">
                          {idle ? '—' : emp.hrsPerWeek.toLocaleString(undefined, { maximumFractionDigits: 1 })}
                        </td>
                        <td className="py-1.5 px-2 text-right text-[12px] text-slate-600 tabular-nums">
                          {idle ? '—' : emp.hrsPerMonth.toLocaleString(undefined, { maximumFractionDigits: 1 })}
                        </td>
                        <td className="py-1.5 px-2 text-left text-[12px] font-medium tabular-nums" style={{ color: idle ? 'var(--fv3-n-200, #CBD5E1)' : '#8B5CF6' }}>
                          {idle ? '—' : emp.fte.toFixed(2)}
                        </td>
                        <td className="py-1.5 pl-2 pr-3.5">
                          <span className="flex items-center gap-1.5">
                            <span className="flex-1 h-[5px] rounded-full bg-slate-200/70 overflow-hidden">
                              <span
                                className="block h-full rounded-full"
                                style={{ width: `${maxHours > 0 ? (emp.hours / maxHours) * 100 : 0}%`, background: 'var(--fv3-n-400, #94A3B8)' }}
                              />
                            </span>
                            <span className="text-[10px] text-slate-400 tabular-nums w-10 text-right">
                              {idle ? 'no hrs' : `${emp.hours.toLocaleString(undefined, { maximumFractionDigits: 0 })}h`}
                            </span>
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </>
          )}
        </div>
      </td>
    </tr>
  );
}

// ─── PositionRow ─────────────────────────────────────────────────────────────

interface PositionRowProps {
  config:             LaborPositionConfig;
  revenueDepartments: string[];
  revenueAccounts:    ForecastAccount[];
  onUpdate:           (updates: Partial<LaborPositionConfig>) => void;
  previewHours:       number | null;
  previewCost:        number | null;
  isOdd:              boolean;
  isExpanded:         boolean;
  onToggleExpand:     () => void;
  recentTotals:       PositionStats['totals'];
}

function PositionRow({
  config, revenueDepartments, revenueAccounts, onUpdate,
  previewHours, previewCost, isOdd, isExpanded, onToggleExpand, recentTotals,
}: PositionRowProps) {
  const [rawValue, setRawValue] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [rawRate, setRawRate] = useState('');
  const [isEditingRate, setIsEditingRate] = useState(false);
  const m = DRIVER_META[config.driverType];

  const displayValue = isEditing ? rawValue : (config.value !== null ? String(config.value) : '');
  const displayRate = isEditingRate
    ? rawRate
    : (config.hourlyRate != null ? String(config.hourlyRate) : '');
  const accountIds = config.revenueAccountIds
    ?? (config.revenueAccountId != null ? [config.revenueAccountId] : []);

  return (
    <tr className="border-b border-slate-100 transition-colors" style={{ background: isOdd ? 'var(--fv3-n-50, #FAFBFC)' : '#fff' }}>

      {/* Position + dept + expand */}
      <td className="py-3 pl-2 pr-2">
        <div className="flex items-start gap-1">
          <button
            type="button"
            onClick={onToggleExpand}
            title={isExpanded ? 'Hide staffing history' : 'Show staffing history — employees, rates, and actual MPOR / HPOR / FTE'}
            className="p-0.5 mt-0.5 rounded transition-colors flex-shrink-0"
            style={{ color: isExpanded ? 'var(--fv3-accent, #F28C38)' : 'var(--fv3-n-400, #94A3B8)', background: isExpanded ? 'var(--fv3-accent-tint, rgba(242,140,56,0.08))' : 'transparent' }}
            onMouseEnter={e => { if (!isExpanded) e.currentTarget.style.background = 'var(--fv3-n-50, #F1F5F9)'; }}
            onMouseLeave={e => { if (!isExpanded) e.currentTarget.style.background = 'transparent'; }}
          >
            {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
          </button>
          <div className="flex flex-col gap-0.5 min-w-0">
            <span className="text-[13px] font-medium text-slate-800 leading-tight">{config.positionName}</span>
            <span className="flex items-center gap-1.5 flex-wrap">
              <span
                className="text-[10px] font-semibold px-1.5 py-0.5 rounded-sm"
                style={{ background: 'var(--fv3-accent-tint, rgba(242,140,56,0.08))', color: 'var(--lodge-orange-500, var(--fv3-accent, #F28C38))' }}
              >
                {config.departmentName}
              </span>
              {recentTotals && recentTotals.headcount > 0 && (
                <span className="text-[10px] text-slate-400 tabular-nums whitespace-nowrap" title="Actuals, last 30 days">
                  {recentTotals.hours.toLocaleString(undefined, { maximumFractionDigits: 0 })} hrs
                  {' · '}{recentTotals.headcount} emp
                  {recentTotals.avgRate != null && <>{' · '}${recentTotals.avgRate.toFixed(2)}/hr</>}
                </span>
              )}
            </span>
          </div>
        </div>
      </td>

      {/* Driver type toggle */}
      <td className="py-3 px-2">
        <DriverTypeToggle value={config.driverType} onChange={t => onUpdate({ driverType: t })} />
      </td>

      {/* Value input */}
      <td className="py-3 px-2">
        <div className="relative flex items-center" style={{ width: 112 }}>
          <input
            type="text"
            inputMode="decimal"
            value={displayValue}
            placeholder="0.00"
            onFocus={() => { setIsEditing(true); setRawValue(config.value !== null ? String(config.value) : ''); }}
            onChange={e => {
              const raw = e.target.value.replace(/[^0-9.]/g, '');
              setRawValue(raw);
              const parsed = parseFloat(raw);
              onUpdate({ value: raw === '' ? null : (isNaN(parsed) ? config.value : parsed) });
            }}
            onBlur={() => setIsEditing(false)}
            className="w-full pr-9 pl-2.5 py-[6px] bg-white border border-slate-200 rounded-md text-[13px] text-right outline-none transition-colors"
            onFocusCapture={e => {
              e.currentTarget.style.borderColor = 'var(--fv3-accent, #f28c38)';
              e.currentTarget.style.boxShadow = '0 0 0 2px var(--fv3-accent-tint, rgba(242,140,56,0.1))';
            }}
            onBlurCapture={e => {
              e.currentTarget.style.borderColor = '';
              e.currentTarget.style.boxShadow = '';
            }}
          />
          <span
            className="absolute right-2 text-[10px] font-semibold pointer-events-none"
            style={{ color: m.color }}
          >
            {m.unit}
          </span>
        </div>
      </td>

      {/* Revenue scope / how-it-computes */}
      <td className="py-3 px-2">
        {config.driverType === 'pct_revenue' ? (
          <RevenueScopePicker
            scope={config.revenueScope}
            deptName={config.revenueDeptName}
            accountIds={accountIds}
            revenueDepartments={revenueDepartments}
            accounts={revenueAccounts}
            onChange={(scope, deptName, ids) =>
              onUpdate({ revenueScope: scope, revenueDeptName: deptName, revenueAccountIds: ids, revenueAccountId: undefined })
            }
          />
        ) : (
          <span className="flex items-center gap-1.5">
            <span className="relative flex items-center" style={{ width: 96 }} title="Hourly rate — auto-filled from recent payroll, adjust as needed">
              <span className="absolute left-2 text-[11px] text-slate-400 pointer-events-none">$</span>
              <input
                type="text"
                inputMode="decimal"
                value={displayRate}
                placeholder="rate"
                onFocus={() => { setIsEditingRate(true); setRawRate(config.hourlyRate != null ? String(config.hourlyRate) : ''); }}
                onChange={e => {
                  const raw = e.target.value.replace(/[^0-9.]/g, '');
                  setRawRate(raw);
                  const parsed = parseFloat(raw);
                  onUpdate({ hourlyRate: raw === '' ? null : (isNaN(parsed) ? config.hourlyRate : parsed) });
                }}
                onBlur={() => setIsEditingRate(false)}
                className="w-full pl-5 pr-7 py-[6px] bg-white border border-slate-200 rounded-md text-[12px] text-right outline-none transition-colors"
                onFocusCapture={e => {
                  e.currentTarget.style.borderColor = 'var(--fv3-accent, #f28c38)';
                  e.currentTarget.style.boxShadow = '0 0 0 2px var(--fv3-accent-tint, rgba(242,140,56,0.1))';
                }}
                onBlurCapture={e => {
                  e.currentTarget.style.borderColor = '';
                  e.currentTarget.style.boxShadow = '';
                }}
              />
              <span className="absolute right-2 text-[10px] text-slate-400 pointer-events-none">/hr</span>
            </span>
            <span title={`${m.long} — ${m.math}`}>
              <Info size={11} className="text-slate-300 flex-shrink-0" />
            </span>
          </span>
        )}
      </td>

      {/* Estimated monthly */}
      <td className="py-3 pl-2 pr-4 text-right">
        {config.value !== null && config.value > 0 ? (
          <div className="flex flex-col items-end gap-0.5">
            {previewHours !== null && (
              <span className="text-[12px] font-semibold text-slate-700">
                {previewHours.toLocaleString(undefined, { maximumFractionDigits: 1 })} hrs
              </span>
            )}
            {previewCost !== null && (
              <span className="text-[11px] text-slate-400">
                ~${Math.round(previewCost).toLocaleString()}
              </span>
            )}
            {previewHours === null && previewCost !== null && (
              <span className="text-[12px] font-semibold text-slate-700">
                ~${Math.round(previewCost).toLocaleString()}
              </span>
            )}
          </div>
        ) : (
          <span className="text-[12px] text-slate-300">—</span>
        )}
      </td>
    </tr>
  );
}

// ─── Main Modal ──────────────────────────────────────────────────────────────

interface LaborForecastDriverModalProps {
  isOpen:                   boolean;
  onClose:                  () => void;
  onApply:                  (configs: LaborPositionConfig[]) => void;
  accountName:              string;
  accountNumber:            string;
  department:               string;
  positions:                LaborPosition[];
  isLoadingPositions?:      boolean;
  accounts?:                ForecastAccount[];       // revenue accounts for % scope picker
  revenueDepartments?:      string[];                // revenue dept names
  occupiedRoomsPerMonth?:   Record<string, number>;  // for preview
  initialConfigs?:          LaborPositionConfig[];
}

export function LaborForecastDriverModal({
  isOpen,
  onClose,
  onApply,
  accountName,
  accountNumber,
  department,
  positions,
  isLoadingPositions = false,
  accounts = [],
  revenueDepartments = [],
  occupiedRoomsPerMonth,
  initialConfigs,
}: LaborForecastDriverModalProps) {
  const modalRef = useRef<HTMLDivElement>(null);
  // In a popped-out pane, grow the window while this modal is open.
  useWideContent(isOpen, 1000 + 120, 820);
  const { state } = useForecastV3();
  const { activeMonth } = useMonthlyVersions();
  const { orgId, locationId } = state;
  const MONTH_KEYS = ['jan','feb','mar','apr','may','jun','jul','aug','sep','oct','nov','dec'];
  const activeMonthKey = activeMonth != null ? MONTH_KEYS[activeMonth - 1] : null;

  const [configs, setConfigs] = useState<LaborPositionConfig[]>([]);

  // Staffing drawer state: which position is open, per-drawer timeframe,
  // and a stats cache keyed by `${positionId}:${range}`
  const [expandedPositionId, setExpandedPositionId] = useState<number | null>(null);
  const [drawerRange, setDrawerRange] = useState<RangeKey>('30d');
  const [statsCache, setStatsCache] = useState<Record<string, PositionStats>>({});
  const [statsLoading, setStatsLoading] = useState(false);

  // Initialise configs when the modal opens or positions change
  useEffect(() => {
    if (!isOpen) return;
    const cfgMap: Record<number, LaborPositionConfig> = {};
    (initialConfigs || []).forEach(c => {
      // Normalize legacy single-account configs to the multi-select shape
      cfgMap[c.positionId] = c.revenueAccountId != null && !c.revenueAccountIds
        ? { ...c, revenueAccountIds: [c.revenueAccountId] }
        : c;
    });

    setConfigs(
      positions.map(pos =>
        cfgMap[pos.id] ?? {
          positionId:    pos.id,
          positionName:  pos.name,
          departmentName: pos.departmentName,
          // Default to hrs_por using the historical HPOR if available
          driverType:    pos.hpor != null ? 'hrs_por' : 'min_por',
          value:         pos.hpor != null ? Math.round(pos.hpor * 1000) / 1000 : null,
          revenueScope:  'total',
        }
      )
    );
    setExpandedPositionId(null);
  }, [isOpen, positions, initialConfigs]);

  // Close on Escape
  useEffect(() => {
    const h = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    const doc = ownerDocumentOf(modalRef.current);
    if (isOpen) doc.addEventListener('keydown', h);
    return () => doc.removeEventListener('keydown', h);
  }, [isOpen, onClose]);

  const updateConfig = useCallback((positionId: number, updates: Partial<LaborPositionConfig>) => {
    setConfigs(prev => prev.map(c => c.positionId === positionId ? { ...c, ...updates } : c));
  }, []);

  // ── Staffing stats fetch — one call for any set of positions ────────────
  const fetchStats = useCallback(async (positionIds: number[], range: RangeKey) => {
    const missing = positionIds.filter(id => !statsCache[`${id}:${range}`]);
    if (missing.length === 0 || !orgId || !locationId) return;
    const days = RANGE_OPTIONS.find(r => r.key === range)?.days ?? 90;
    const end = new Date();
    const start = new Date(end.getTime() - (days - 1) * 86400000);
    const fmt = (d: Date) => d.toISOString().slice(0, 10);

    setStatsLoading(true);
    try {
      const res = await api.get(
        `${API_ENDPOINTS.LABOR_POSITION_STATS}?orgId=${orgId}&locationId=${locationId}&positionIds=${missing.join(',')}&startDate=${fmt(start)}&endDate=${fmt(end)}`
      );
      const data = res.data;
      setStatsCache(prev => {
        const next = { ...prev };
        for (const id of missing) {
          const posEntry = (data?.positions || []).find((p: any) => p.positionId === id);
          next[`${id}:${range}`] = {
            days: data?.days ?? days,
            occupiedRooms: data?.occupiedRooms ?? 0,
            dates: data?.dates ?? [],
            occupiedRoomsByDay: data?.occupiedRoomsByDay ?? [],
            coverage: posEntry?.coverage ?? null,
            totals: posEntry?.totals ?? null,
            employees: posEntry?.employees ?? [],
          };
        }
        return next;
      });
    } catch (err) {
      console.error('Failed to load labor position stats', err);
      setStatsCache(prev => {
        const next = { ...prev };
        for (const id of missing) {
          next[`${id}:${range}`] = { days, occupiedRooms: 0, dates: [], occupiedRoomsByDay: [], coverage: null, totals: null, employees: [] };
        }
        return next;
      });
    } finally {
      setStatsLoading(false);
    }
  }, [statsCache, orgId, locationId]);

  // Prefetch all positions on open (default 30d window) — powers the sort
  // order and the per-row totals without expanding anything
  useEffect(() => {
    if (isOpen && positions.length > 0) {
      fetchStats(positions.map(p => p.id), '30d');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, positions, orgId, locationId]);

  useEffect(() => {
    if (expandedPositionId != null) fetchStats([expandedPositionId], drawerRange);
  }, [expandedPositionId, drawerRange, fetchStats]);

  // Auto-populate hourly rates from recent actuals (30d, all pay codes) for
  // any config that doesn't already carry a saved or user-entered rate
  useEffect(() => {
    setConfigs(prev => {
      let changed = false;
      const next = prev.map(c => {
        if (c.hourlyRate == null) {
          const rate = statsCache[`${c.positionId}:30d`]?.totals?.avgRate;
          if (rate && rate > 0) { changed = true; return { ...c, hourlyRate: rate }; }
        }
        return c;
      });
      return changed ? next : prev;
    });
  }, [statsCache]);

  const toggleExpand = useCallback((positionId: number) => {
    setExpandedPositionId(prev => prev === positionId ? null : positionId);
  }, []);

  // Estimated occupied rooms for the active month (or rolling average)
  const previewOccupied = useMemo(() => {
    if (occupiedRoomsPerMonth) {
      if (activeMonthKey && occupiedRoomsPerMonth[activeMonthKey]) {
        return occupiedRoomsPerMonth[activeMonthKey];
      }
      const vals = Object.values(occupiedRoomsPerMonth).filter(Boolean);
      if (vals.length) return vals.reduce((a, b) => a + b, 0) / vals.length;
    }
    return 3200; // sensible hotel default
  }, [occupiedRoomsPerMonth, activeMonthKey]);

  // Total revenue for preview
  const totalRevenue = useMemo(() => {
    const rev = accounts.filter(a => a.accountType === 'Revenue');
    if (!rev.length) return 485_000;
    const sum = rev.reduce((s, a) => {
      const v = activeMonthKey
        ? Math.abs(a.monthlyValues[activeMonthKey] ?? 0)
        : Object.values(a.monthlyValues || {}).reduce((x, y) => x + Math.abs(y), 0) / 12;
      return s + v;
    }, 0);
    return sum || 485_000;
  }, [accounts, activeMonthKey]);

  const getRevenueBase = useCallback((cfg: LaborPositionConfig): number => {
    if (cfg.revenueScope === 'total') return totalRevenue;
    if (cfg.revenueScope === 'department' && cfg.revenueDeptName) {
      const base = accounts
        .filter(a => a.accountType === 'Revenue' && a.department === cfg.revenueDeptName)
        .reduce((s, a) => {
          const v = activeMonthKey
            ? Math.abs(a.monthlyValues[activeMonthKey] ?? 0)
            : Object.values(a.monthlyValues || {}).reduce((x, y) => x + Math.abs(y), 0) / 12;
          return s + v;
        }, 0);
      return base || totalRevenue;
    }
    if (cfg.revenueScope === 'account') {
      const ids = cfg.revenueAccountIds
        ?? (cfg.revenueAccountId != null ? [cfg.revenueAccountId] : []);
      if (ids.length > 0) {
        return ids.reduce((s, id) => {
          const a = accounts.find(x => x.chartOfAccountId === id);
          if (!a) return s;
          return s + Math.abs(activeMonthKey ? (a.monthlyValues[activeMonthKey] ?? 0) : Object.values(a.monthlyValues || {}).reduce((x, y) => x + Math.abs(y), 0) / 12);
        }, 0);
      }
    }
    return totalRevenue;
  }, [accounts, totalRevenue, activeMonthKey]);

  const getPreview = useCallback((cfg: LaborPositionConfig, pos: LaborPosition): { hours: number | null; cost: number | null } => {
    if (!cfg.value || cfg.value <= 0) return { hours: null, cost: null };
    // Hourly rate: the config's saved/entered rate (same one the calculator
    // will use), then recent actuals, then position hints, then $18 default
    const rate = (cfg.hourlyRate != null && cfg.hourlyRate > 0 ? cfg.hourlyRate : null)
      ?? statsCache[`${cfg.positionId}:30d`]?.totals?.avgRate
      ?? pos.avgHourlyRate
      ?? (pos.hpor && pos.dpor ? pos.dpor / pos.hpor : 18);

    if (cfg.driverType === 'min_por') {
      const hours = (cfg.value / 60) * previewOccupied;
      return { hours, cost: hours * rate };
    }
    if (cfg.driverType === 'hrs_por') {
      const hours = cfg.value * previewOccupied;
      return { hours, cost: hours * rate };
    }
    if (cfg.driverType === 'fte') {
      const hours = (cfg.value * FTE_HOURS_PER_YEAR) / 12;
      return { hours, cost: hours * rate };
    }
    if (cfg.driverType === 'pct_revenue') {
      const cost = getRevenueBase(cfg) * (cfg.value / 100);
      return { hours: null, cost };
    }
    return { hours: null, cost: null };
  }, [previewOccupied, getRevenueBase, statsCache]);

  const totals = useMemo(() =>
    configs.reduce((acc, cfg) => {
      const pos = positions.find(p => p.id === cfg.positionId);
      if (!pos) return acc;
      const { hours, cost } = getPreview(cfg, pos);
      return { hours: acc.hours + (hours ?? 0), cost: acc.cost + (cost ?? 0) };
    }, { hours: 0, cost: 0 }),
    [configs, positions, getPreview]
  );

  // Display order: busiest positions first (actual hours, last 30 days);
  // positions with no payroll history sink to the bottom
  const displayConfigs = useMemo(() => {
    const hoursOf = (c: LaborPositionConfig) => statsCache[`${c.positionId}:30d`]?.totals?.hours ?? -1;
    return [...configs].sort((a, b) => hoursOf(b) - hoursOf(a));
  }, [configs, statsCache]);

  const configuredCount   = configs.filter(c => c.value !== null && c.value > 0).length;
  const revenueAccounts   = accounts.filter(a => a.accountType === 'Revenue');
  const monthLabel        = activeMonthKey
    ? activeMonthKey.charAt(0).toUpperCase() + activeMonthKey.slice(1)
    : 'Month';

  const handleApply = () => {
    onApply(configs);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div ref={modalRef} className="fixed inset-0 z-[9999] flex items-center justify-center">
      {/* Backdrop */}
      <div className="fv3-scrim absolute inset-0 bg-black/50" onClick={onClose} />

      {/* Modal */}
      <div className="fv3-dialog relative bg-white rounded-lg shadow-xl border border-slate-200 w-full max-h-[90vh] flex flex-col overflow-hidden" style={{ maxWidth: 'min(1000px, calc(100vw - 32px))' }}>

        {/* Header */}
        <div className="fv3-dialog-head px-5 py-4 border-b border-slate-200 flex items-start justify-between">
          <div>
            <div className="flex items-center gap-2.5">
              <Users size={20} className="text-rose-500 flex-shrink-0" />
              <h2
                className="text-lg font-semibold text-slate-800"
                data-fv3-title="" style={{ fontFamily: "'DM Serif Display', serif" }}
              >
                Labor Driver Configuration
              </h2>
              {positions.length > 0 && (
                <span
                  className="px-2 py-0.5 rounded text-[11px] font-semibold"
                  style={{ background: 'var(--fv3-accent-tint, rgba(242,140,56,0.08))', color: 'var(--lodge-orange-500)' }}
                >
                  {configuredCount} / {configs.length} CONFIGURED
                </span>
              )}
            </div>
            <p className="text-xs text-slate-400 mt-1">
              Set a per-position labor driver — expand a position to see its actual staffing and adopt a value from payroll history
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-1 hover:bg-slate-100 rounded transition-colors"
          >
            <X size={18} className="text-slate-400" />
          </button>
        </div>

        {/* Context bar — single compact line */}
        <div className="px-5 py-2 bg-slate-50 border-b border-slate-100 flex items-center gap-3 text-sm min-w-0">
          <div className="flex items-center gap-1.5 min-w-0">
            <span className="text-[10px] text-slate-400 font-medium uppercase tracking-wide flex-shrink-0">Account</span>
            <span className="text-[12px] text-slate-800 font-medium truncate">{accountNumber} · {accountName}</span>
          </div>
          <div className="w-px h-3.5 bg-slate-200 flex-shrink-0" />
          <div className="flex items-center gap-1.5 flex-shrink-0">
            <span className="text-[10px] text-slate-400 font-medium uppercase tracking-wide">Dept</span>
            <span className="text-[12px] text-slate-800 font-medium">{department}</span>
          </div>
          <div className="w-px h-3.5 bg-slate-200 flex-shrink-0" />
          <span
            className="text-[10px] font-semibold px-1.5 py-0.5 rounded flex-shrink-0"
            style={{ background: 'rgba(16,185,129,0.08)', color: '#10B981' }}
          >
            {positions.length} MAPPED
          </span>
          {occupiedRoomsPerMonth && (
            <span className="text-[11px] text-slate-500 ml-auto flex-shrink-0 tabular-nums">
              Preview: {monthLabel} · ~{Math.round(previewOccupied).toLocaleString()} occ rooms
            </span>
          )}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-auto">

          {/* Loading */}
          {isLoadingPositions && (
            <div className="flex items-center justify-center py-16">
              <Loader2 size={22} className="animate-spin mr-2" style={{ color: 'var(--lodge-orange-400)' }} />
              <span className="text-sm text-slate-500">Loading positions…</span>
            </div>
          )}

          {/* Empty state */}
          {!isLoadingPositions && positions.length === 0 && (
            <div className="flex flex-col items-center justify-center py-16 px-10 text-center">
              <AlertCircle size={40} className="text-slate-200 mb-3" />
              <h3 className="text-sm font-semibold text-slate-600 mb-1.5">No positions mapped to this account</h3>
              <p className="text-xs text-slate-400 max-w-xs leading-relaxed">
                Use <strong>Position → GL Mapping</strong> in Settings to associate payroll positions
                with this GL account, then return here to configure per-position labor drivers.
              </p>
            </div>
          )}

          {/* Table */}
          {!isLoadingPositions && positions.length > 0 && (
            <>
              {/* Column headers */}
              <table className="w-full" style={{ tableLayout: 'fixed' }}>
                <colgroup>
                  <col style={{ width: '25%' }} />
                  <col style={{ width: '21%' }} />
                  <col style={{ width: '13%' }} />
                  <col style={{ width: '25%' }} />
                  <col style={{ width: '16%' }} />
                </colgroup>
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="text-left py-2 pl-4 pr-2 text-[10px] font-semibold text-slate-400 uppercase tracking-wider">
                      Position
                    </th>
                    <th className="text-left py-2 px-2 text-[10px] font-semibold text-slate-400 uppercase tracking-wider">
                      Driver Type
                    </th>
                    <th className="text-left py-2 px-2 text-[10px] font-semibold text-slate-400 uppercase tracking-wider">
                      Value
                    </th>
                    <th className="text-left py-2 px-2 text-[10px] font-semibold text-slate-400 uppercase tracking-wider">
                      Rate / Basis
                    </th>
                    <th className="text-right py-2 pl-2 pr-4 text-[10px] font-semibold text-slate-400 uppercase tracking-wider">
                      Est. / {monthLabel}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {displayConfigs.map((cfg, i) => {
                    const pos = positions.find(p => p.id === cfg.positionId);
                    if (!pos) return null;
                    const { hours, cost } = getPreview(cfg, pos);
                    const isExpanded = expandedPositionId === cfg.positionId;
                    return (
                      <React.Fragment key={cfg.positionId}>
                        <PositionRow
                          config={cfg}
                          revenueDepartments={revenueDepartments}
                          revenueAccounts={revenueAccounts}
                          onUpdate={u => updateConfig(cfg.positionId, u)}
                          previewHours={hours}
                          previewCost={cost}
                          isOdd={i % 2 !== 0}
                          isExpanded={isExpanded}
                          onToggleExpand={() => toggleExpand(cfg.positionId)}
                          recentTotals={statsCache[`${cfg.positionId}:30d`]?.totals ?? null}
                        />
                        {isExpanded && (
                          <StaffingDrawer
                            positionId={cfg.positionId}
                            stats={statsCache[`${cfg.positionId}:${drawerRange}`] ?? null}
                            isLoading={statsLoading && !statsCache[`${cfg.positionId}:${drawerRange}`]}
                            range={drawerRange}
                            onRangeChange={setDrawerRange}
                            onUseValue={(driverType, value) => {
                            const updates: Partial<LaborPositionConfig> = { driverType, value };
                            // Adopting a benchmark also adopts that window's actual rate
                            const windowRate = statsCache[`${cfg.positionId}:${drawerRange}`]?.totals?.avgRate;
                            if (windowRate && windowRate > 0) updates.hourlyRate = windowRate;
                            updateConfig(cfg.positionId, updates);
                          }}
                          />
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>

              {/* Totals bar */}
              {(totals.hours > 0 || totals.cost > 0) && (
                <div className="border-t-2 border-slate-200 bg-slate-800 text-white px-5 py-3 flex items-center justify-between">
                  <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">
                    Total Estimated · {monthLabel}
                  </span>
                  <div className="flex items-center gap-6">
                    {totals.hours > 0 && (
                      <div className="text-right">
                        <div className="text-[10px] text-slate-400 mb-0.5">Hours</div>
                        <div className="text-[14px] font-bold">
                          {totals.hours.toLocaleString(undefined, { maximumFractionDigits: 1 })}
                        </div>
                      </div>
                    )}
                    {totals.cost > 0 && (
                      <div className="text-right">
                        <div className="text-[10px] text-slate-400 mb-0.5">Est. Cost</div>
                        <div className="text-[14px] font-bold">
                          ${Math.round(totals.cost).toLocaleString()}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="fv3-dialog-foot px-5 py-3.5 border-t border-slate-200 flex items-center justify-between">
          <span className="text-xs text-slate-400">
            Drivers apply to open forecast months · closed months use actual payroll
          </span>
          <div className="flex gap-2">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-slate-200 rounded-md text-[13px] font-medium text-slate-500 hover:bg-slate-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleApply}
              disabled={positions.length === 0}
              className="px-5 py-2 rounded-md text-[13px] font-semibold text-white flex items-center gap-1.5 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              style={{
                background: 'var(--accent, var(--fv3-accent, #FF7A00))',
                boxShadow:  '0 1px 3px var(--fv3-accent-shadow, rgba(255,122,0,0.3))',
              }}
              onMouseEnter={e => { if (!e.currentTarget.disabled) e.currentTarget.style.background = 'var(--fv3-accent-hover, #E56E00)'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'var(--accent, var(--fv3-accent, #FF7A00))'; }}
            >
              <Check size={14} /> Apply Drivers
            </button>
          </div>
        </div>

        {/* Tip */}
        <div className="mx-5 mb-4 px-4 py-2.5 bg-blue-50/80 rounded-md border border-blue-100 flex items-start gap-2">
          <Info size={14} className="text-blue-500 flex-shrink-0 mt-0.5" />
          <span className="text-xs text-blue-800 leading-relaxed">
            <strong>MPOR</strong> (minutes per occupied room) and <strong>HPOR</strong> (hours per occupied room) scale hours with occupancy.
            {' '}<strong>FTE</strong> is fixed staffing: FTEs × 2,080 hrs/yr ÷ 12 per month, regardless of occupancy.
            {' '}<strong>% Rev</strong> is best where cost tracks revenue. Hours are costed at each position's average hourly
            rate from payroll (all pay codes). Previews are estimates — actuals use real occupancy and payroll.
          </span>
        </div>
      </div>
    </div>
  );
}

export default LaborForecastDriverModal;
